/**
 * Chantier : - (feedback in-app, hors chantier structure)
 * Regle metier : - (outil interne, hors domaine)
 * Decision : spec PLAN/specifications-feedback-inapp.md (barreau 1)
 * Declencheur (icone aide dans le header) + modale de feedback. Gate admin_serme.
 * Capture route + identite depuis la session et envoie via submitFeedback. Best-effort :
 * un echec affiche un toast, le ticket n'est pas mis en file (cf. spec, pas de retry).
 */
import { useState } from "react";
import * as Sentry from "@sentry/react";
import { HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "@/hooks/AuthProvider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog";
import {
  DARK_HEADER_CLOSE,
  WIZARD_DIALOG_HEADER_NAVY,
  WIZARD_DIALOG_TITLE,
  WIZARD_DIALOG_DESCRIPTION,
} from "@/lib/dialogStyles";
import { OBJET_TYPE_DEMANDE_LABELS } from "@/lib/labels";
import type { ObjetType } from "@/schemas/demande";
import { submitFeedback, FeedbackConfigError, type FeedbackType } from "@/services/feedback";

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "evolution", label: "Évolution" },
];

type UserType = "admin" | "client" | "autre";
const USER_TYPE_OPTIONS: { value: UserType; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "client", label: "Client" },
  { value: "autre", label: "Autre" },
];

const LABEL_CLASS = "text-overline font-semibold uppercase text-muted-foreground";

function nowLocalDatetime(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function FeedbackWidget() {
  const { appRole, userData, user } = useAuthContext();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>("bug");
  const [userTypes, setUserTypes] = useState<UserType[]>([]);
  const [userTypeAutre, setUserTypeAutre] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [description, setDescription] = useState("");
  const [objetType, setObjetType] = useState<ObjetType | undefined>(undefined);
  const [objetRef, setObjetRef] = useState("");
  const [dateIncident, setDateIncident] = useState("");
  const [messageErreur, setMessageErreur] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Emetteurs = admin_serme uniquement (cf. spec, hypothese RGPD allegee).
  if (appRole !== "admin_serme") return null;

  const reset = () => {
    setType("bug");
    setUserTypes([]);
    setUserTypeAutre("");
    setPageUrl("");
    setDescription("");
    setObjetType(undefined);
    setObjetRef("");
    setDateIncident("");
    setMessageErreur("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const toggleUserType = (v: UserType) =>
    setUserTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const canSubmit =
    description.trim().length > 0 &&
    userTypes.length > 0 &&
    (!userTypes.includes("autre") || userTypeAutre.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Complète la description et le type d'utilisateur");
      return;
    }
    const typeUtilisateur = userTypes
      .map((t) => (t === "admin" ? "Admin" : t === "client" ? "Client" : `Autre : ${userTypeAutre.trim()}`))
      .join(", ");
    const isBug = type === "bug";
    setSubmitting(true);
    try {
      await submitFeedback({
        type,
        typeUtilisateur,
        pageUrl: pageUrl.trim(),
        description: description.trim(),
        route: location.pathname,
        reporterNom: userData ? `${userData.prenom} ${userData.nom}` : "",
        reporterEmail: user?.email ?? "",
        objetType: isBug && objetType ? OBJET_TYPE_DEMANDE_LABELS[objetType] : "",
        objetRef: isBug ? objetRef.trim() : "",
        dateIncident: isBug ? dateIncident : "",
        messageErreur: isBug ? messageErreur.trim() : "",
      });
      toast.success("Retour envoyé. Les tickets sont consultables dans l'espace Notion.");
      handleOpenChange(false);
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "feedback" } });
      if (err instanceof FeedbackConfigError) {
        toast.error("Feedback non configuré — préviens un développeur.");
      } else {
        toast.error("Échec de l'envoi. Réessaie dans un instant.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label="Envoyer un retour"
        className="text-header-muted hover:text-white hover:bg-white/10 transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </Button>

      <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
        <ResponsiveDialogContent
          desktopClassName="sm:max-w-lg"
          className="p-0 overflow-hidden border-none bg-card shadow-2xl max-h-[90vh] flex flex-col"
          closeClassName={DARK_HEADER_CLOSE}
        >
          <ResponsiveDialogHeader className={WIZARD_DIALOG_HEADER_NAVY}>
            <ResponsiveDialogTitle className={WIZARD_DIALOG_TITLE}>
              Envoyer un retour
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription className={WIZARD_DIALOG_DESCRIPTION}>
              Signaler un bug ou une évolution
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="grid gap-5 px-6 py-5 flex-1 overflow-y-auto min-h-0">
            <div className="space-y-2">
              <Label className={LABEL_CLASS}>Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {TYPE_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={type === opt.value ? "brand" : "outline"}
                    onClick={() => setType(opt.value)}
                    className="h-10 font-semibold text-xs uppercase tracking-widest"
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className={LABEL_CLASS}>
                Pour quels types d&apos;utilisateur ?{" "}
                <span className="text-muted-foreground/60 normal-case">(plusieurs possibles)</span>
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {USER_TYPE_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={userTypes.includes(opt.value) ? "brand" : "outline"}
                    onClick={() => toggleUserType(opt.value)}
                    className="h-10 font-semibold text-xs uppercase tracking-widest"
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              {userTypes.includes("autre") && (
                <Input
                  placeholder="Préciser..."
                  value={userTypeAutre}
                  onChange={(e) => setUserTypeAutre(e.target.value)}
                  className="bg-muted border-none font-medium"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label className={LABEL_CLASS}>
                URL de la page concernée{" "}
                <span className="text-muted-foreground/60 normal-case">(optionnel)</span>
              </Label>
              <Input
                placeholder="Colle l'URL de la page ici"
                value={pageUrl}
                onChange={(e) => setPageUrl(e.target.value)}
                className="bg-muted border-none font-medium"
              />
            </div>

            <div className="space-y-2">
              <Label className={LABEL_CLASS}>Description</Label>
              <p className="text-xs text-muted-foreground">
                {type === "bug"
                  ? "1. Décris les actions qui déclenchent le problème. 2. Puis le problème lui-même, le plus précisément possible."
                  : "Décris l'évolution souhaitée et le besoin auquel elle répond."}
              </p>
              <Textarea
                placeholder={
                  type === "bug"
                    ? "Ex. Depuis la fiche chantier, je clique sur Valider, puis..."
                    : "Décris l'évolution souhaitée..."
                }
                className="min-h-[120px] bg-muted border-none font-medium p-3 resize-none"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {type === "bug" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={LABEL_CLASS}>Type d&apos;objet</Label>
                    <Select value={objetType ?? ""} onValueChange={(v) => setObjetType(v as ObjetType)}>
                      <SelectTrigger className="bg-muted border-none font-bold">
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(OBJET_TYPE_DEMANDE_LABELS) as ObjetType[]).map((t) => (
                          <SelectItem key={t} value={t}>
                            {OBJET_TYPE_DEMANDE_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className={LABEL_CLASS}>Référence</Label>
                    <Input
                      placeholder="Ex. CH-0012"
                      value={objetRef}
                      onChange={(e) => setObjetRef(e.target.value)}
                      className="bg-muted border-none font-medium"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={LABEL_CLASS}>Date / heure de l&apos;incident</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="datetime-local"
                      value={dateIncident}
                      onChange={(e) => setDateIncident(e.target.value)}
                      className="bg-muted border-none font-medium"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDateIncident(nowLocalDatetime())}
                      className="shrink-0 font-semibold text-xs uppercase tracking-widest"
                    >
                      À l&apos;instant
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={LABEL_CLASS}>Message d&apos;erreur vu</Label>
                  <Textarea
                    placeholder="Copie ici le message d'erreur affiché, le cas échéant..."
                    className="min-h-[70px] bg-muted border-none font-medium p-3 resize-none"
                    value={messageErreur}
                    onChange={(e) => setMessageErreur(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          <ResponsiveDialogFooter className="border-t border-border/50 bg-muted/30 flex items-center justify-end gap-3 px-6 py-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              className="font-bold uppercase text-overline"
            >
              Fermer
            </Button>
            <Button
              type="button"
              variant="brand"
              loading={submitting}
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
              className="font-bold uppercase text-overline px-8 h-11"
            >
              Envoyer
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
}
